import type { AppType } from '@snapchat/blizzard-schema';
import type { CollectionMode } from './initBlizzard';

/**
 * Properties related to the application environment that Blizzard analytics are being used in.
 * Mostly applicable to applications like dWeb for which these properties make sense.
 *
 * See the App Taxonomy (go/app-taxonomy) for more details on how to fill these out.
 */
export interface AppEnvironment {
  /** The web application (e.g. SNAPCHAT). Note: not currently attached to events. */
  application?: Application;

  /**
   * The specific type of the application running. This can be either WEB_DESKTOP for
   * desktop-optimized web apps, or WEB_MOBILE for mobile-optimized web apps.
   */
  appType: WebAppType;

  /** The build of the application (BETA, PROD, etc) */
  appBuild: AppBuild;

  /** The version of the application (11.81.5 etc) */
  appVersion?: string;
}

/**
 * The set of specific web applications. TODO: when adoption of this grows beyond dWeb, codify this
 * in the schema and use the schema type.
 */
export type Application = 'SNAPCHAT';

/** Subset of the schema AppTypes applicable for web apps. */
export type WebAppType = ('WEB_DESKTOP' | 'WEB_MOBILE') & AppType;

/**
 * Build of the app as reported in Blizzard events. To be consistent with mobile implementation we
 * retain 'MASTER' as the identifier for the internal build.
 */
export type AppBuild = 'ALPHA' | 'BETA' | 'DEBUG' | 'MASTER' | 'PROD';

let environment: AppEnvironment | undefined;

/**
 * Set the {@link AppEnvironment}.
 *
 * @internal
 */
export function setAppEnvironment(appEnvironment: AppEnvironment): void {
  environment = appEnvironment;
}

/**
 * Retrieve the {@link AppEnvironment}.
 *
 * @internal
 */
export function getAppEnvironment(): AppEnvironment {
  if (!environment) {
    throw new Error('appEnvironment not set: was Blizzard initialized?');
  }
  return environment;
}

let collectionMode: CollectionMode;

/**
 * Set the {@link CollectionMode}
 *
 * @internal
 */
export function setCollectionMode(mode: CollectionMode): void {
  collectionMode = mode;
}

/**
 * Returns the {@link CollectionMode}
 *
 * @internal
 */
export function getCollectionMode(): CollectionMode {
  if (!collectionMode) {
    throw new Error('collectionMode not set: was Blizzard initialized?');
  }
  return collectionMode;
}
